export interface StreamingResponseForDisplay {
  text: string
  isStreaming: boolean
  streamStartTime?: number
}

/**
 * Aggressive buffering configuration.
 * Waits until content is suspected to be meaningful "commentary" before showing.
 */
export const RESPONSE_BUFFER_CONFIG = {
  MIN_WORDS_STANDARD: 40,
  MIN_WORDS_CODE: 15,
  MIN_WORDS_LIST: 20,
  MIN_WORDS_QUESTION: 8,
  MIN_WORDS_HEADER: 12,
  MIN_BUFFER_MS: 500,
  MAX_BUFFER_MS: 2500,
  TIMEOUT_MIN_WORDS: 5,
  HIGH_WORD_COUNT: 60,
  STREAM_REVEAL_TICK_MS: 40,
  STREAM_REVEAL_MEDIUM_TICK_MS: 56,
  STREAM_REVEAL_LONG_TICK_MS: 80,
  STREAM_REVEAL_MEDIUM_TEXT_CHARS: 2000,
  STREAM_REVEAL_LONG_TEXT_CHARS: 8000,
  STREAM_REVEAL_MIN_CHARS_PER_TICK: 3,
  STREAM_REVEAL_MAX_CHARS_PER_TICK: 56,
  STREAM_REVEAL_LONG_MAX_CHARS_PER_TICK: 128,
  STREAM_REVEAL_BACKLOG_RATIO: 0.35,
  COMPLETE_REVEAL_CHARS_PER_TICK: 12,
  COMPLETE_REVEAL_TICK_MS: 32,
} as const

type BufferReason =
  | 'complete'
  | 'streaming_content'
  | 'min_time'
  | 'timeout'
  | 'code_block'
  | 'list'
  | 'header'
  | 'question'
  | 'threshold_met'
  | 'high_word_count'
  | 'buffering'

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(w => w.length > 0).length
}

function hasCodeBlock(text: string): boolean {
  return /```/.test(text)
}

function hasList(text: string): boolean {
  return /^\s*[-*+]\s/m.test(text) || /^\s*\d+\.\s/m.test(text)
}

function hasHeader(text: string): boolean {
  return /^#{1,4}\s/m.test(text)
}

function hasStructure(text: string): boolean {
  if (/[.!?:]\s*$/.test(text.trimEnd())) return true
  if (/\n\s*\n/.test(text)) return true
  if (/\n\s*#{1,4}\s/.test(text)) return true
  if (hasCodeBlock(text)) return true
  return false
}

function isQuestion(text: string): boolean {
  return /\?\s*$/.test(text.trim())
}

export function shouldShowStreamingContent(
  text: string,
  isStreaming: boolean,
  streamStartTime?: number
): { shouldShow: boolean; reason: BufferReason; wordCount: number } {
  const wordCount = countWords(text)

  if (!isStreaming) {
    return { shouldShow: true, reason: 'complete', wordCount }
  }

  // Once text has arrived, show it immediately so the chat visibly streams.
  if (text.trim().length > 0) {
    return { shouldShow: true, reason: 'streaming_content', wordCount }
  }

  const elapsed = streamStartTime ? Date.now() - streamStartTime : 0

  if (elapsed < RESPONSE_BUFFER_CONFIG.MIN_BUFFER_MS) {
    return { shouldShow: false, reason: 'min_time', wordCount }
  }

  if (elapsed > RESPONSE_BUFFER_CONFIG.MAX_BUFFER_MS && wordCount >= RESPONSE_BUFFER_CONFIG.TIMEOUT_MIN_WORDS) {
    return { shouldShow: true, reason: 'timeout', wordCount }
  }

  if (hasCodeBlock(text) && wordCount >= RESPONSE_BUFFER_CONFIG.MIN_WORDS_CODE) {
    return { shouldShow: true, reason: 'code_block', wordCount }
  }

  if (hasHeader(text) && wordCount >= RESPONSE_BUFFER_CONFIG.MIN_WORDS_HEADER) {
    return { shouldShow: true, reason: 'header', wordCount }
  }

  if (hasList(text) && wordCount >= RESPONSE_BUFFER_CONFIG.MIN_WORDS_LIST) {
    return { shouldShow: true, reason: 'list', wordCount }
  }

  if (isQuestion(text) && wordCount >= RESPONSE_BUFFER_CONFIG.MIN_WORDS_QUESTION) {
    return { shouldShow: true, reason: 'question', wordCount }
  }

  if (wordCount >= RESPONSE_BUFFER_CONFIG.MIN_WORDS_STANDARD && hasStructure(text)) {
    return { shouldShow: true, reason: 'threshold_met', wordCount }
  }

  if (wordCount >= RESPONSE_BUFFER_CONFIG.HIGH_WORD_COUNT) {
    return { shouldShow: true, reason: 'high_word_count', wordCount }
  }

  return { shouldShow: false, reason: 'buffering', wordCount }
}

export function isResponseBufferingForDisplay(response: StreamingResponseForDisplay | undefined): boolean {
  if (!response) return false
  if (!response.isStreaming) return false
  const decision = shouldShowStreamingContent(response.text, response.isStreaming, response.streamStartTime)
  return !decision.shouldShow
}

export function shouldRevealCompletedText(previousText: string, nextText: string): boolean {
  return previousText.length === 0 && nextText.trim().length > RESPONSE_BUFFER_CONFIG.COMPLETE_REVEAL_CHARS_PER_TICK
}

export function shouldContinueRevealAfterStreaming(
  displayedText: string,
  nextText: string,
  streamedText: string = ''
): boolean {
  const hasVisibleProgress = displayedText.length > 0
  const hasStreamedProgress = streamedText.length > 0 && nextText.startsWith(streamedText)

  return (hasVisibleProgress || hasStreamedProgress) &&
    displayedText.length < nextText.length &&
    nextText.startsWith(displayedText)
}

export function shouldRevealFreshCompletedText(
  previousText: string,
  nextText: string,
  messageTimestamp: number | undefined,
  now: number = Date.now()
): boolean {
  const isFreshCompletion = messageTimestamp != null && now - messageTimestamp < 5000
  return isFreshCompletion && shouldRevealCompletedText(previousText, nextText)
}

function safeSlice(text: string, end: number): string {
  const boundedEnd = Math.max(0, Math.min(text.length, end))
  if (boundedEnd <= 0) return ''

  const lastCodeUnit = text.charCodeAt(boundedEnd - 1)
  const endsWithHighSurrogate = lastCodeUnit >= 0xD800 && lastCodeUnit <= 0xDBFF
  return text.slice(0, endsWithHighSurrogate ? boundedEnd - 1 : boundedEnd)
}

export function getStreamingTextRevealIntervalMs(text: string): number {
  if (text.length >= RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_LONG_TEXT_CHARS) {
    return RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_LONG_TICK_MS
  }

  if (text.length >= RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_MEDIUM_TEXT_CHARS) {
    return RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_MEDIUM_TICK_MS
  }

  return RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_TICK_MS
}

export function getStreamingTextRevealSlice(currentText: string, nextText: string): string {
  if (currentText === nextText) return currentText
  if (currentText.length > nextText.length || !nextText.startsWith(currentText)) {
    return nextText
  }

  const remaining = nextText.length - currentText.length
  const dynamicStep = Math.ceil(remaining * RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_BACKLOG_RATIO)
  const maxStep = nextText.length >= RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_LONG_TEXT_CHARS
    ? RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_LONG_MAX_CHARS_PER_TICK
    : RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_MAX_CHARS_PER_TICK
  const step = Math.min(
    remaining,
    Math.max(
      RESPONSE_BUFFER_CONFIG.STREAM_REVEAL_MIN_CHARS_PER_TICK,
      Math.min(maxStep, dynamicStep)
    )
  )

  return safeSlice(nextText, currentText.length + step)
}

export function getCompletedTextRevealSlice(text: string, visibleLength: number): string {
  const nextLength = Math.min(
    text.length,
    visibleLength + RESPONSE_BUFFER_CONFIG.COMPLETE_REVEAL_CHARS_PER_TICK
  )
  return safeSlice(text, nextLength)
}

export interface ScrollMetrics {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
}

export function isScrollNearBottom(metrics: ScrollMetrics, thresholdPx = 24): boolean {
  return metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= thresholdPx
}
